/* eslint-disable sort-imports-es6-autofix/sort-imports-es6 */
import { glossary } from '../../../../localization/global';
import { NullPromise } from '../../../../webmodule-common/interop/webmodule-interop';
import {
  currencyCodeToString,
  moneyToCompactStr,
  stringToCurrencyCode
} from '../../../../webmodule-common/other/currency-formatter';
import { localDateTimeToServer } from '../../../../webmodule-common/other/datetime-converter';
import { tlang } from '../../../../webmodule-common/other/language/lang';
import { QuoteState } from '../../common/object-states';
import { ResultGetGeneratedQuoteRevenue } from '../../../api/supplier-api-interface-franchiseenetwork';
import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import { generatedQuotesCache } from '../data/generated-quotes-cache';
import { ApexCharts, ApexChartWidget } from './apex-chart-widget';
import { customElement, property } from 'lit/decorators.js';

@customElement('generated-quotes-revenue-widget')
export class GeneratedQuotesWidget extends ApexChartWidget {
  @property({ type: Array }) branchIds?: string[] = [];
  @property() userId?: string;
  @property({ type: Array }) states: number[] = [
    QuoteState.Draft,
    QuoteState.Active,
    QuoteState.IssuePending,
    QuoteState.Issued,
    QuoteState.Accepted,
    QuoteState.Accepted_AssignedToReviewer,
    QuoteState.SupplierReviewPending,
    QuoteState.SupplierReviewed,
    QuoteState.Approved
  ];

  private _cache = generatedQuotesCache();

  // Track extra per-currency charts and connection state
  private charts: any[] = [];
  private _isDisconnected = false;

  constructor() {
    super('quote-issued-revenue');
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._isDisconnected = true;
    try { this.chart?.destroy?.(); } catch (e) { /* noop */ }
    this.chart = null;
    for (const c of this.charts) { try { c?.destroy?.(); } catch (e) { /* noop */ } }
    this.charts = [];
  }

  protected async refreshData(): NullPromise<ResultGetGeneratedQuoteRevenue> {
    if (this.dateRange) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      if (this.branchIds) {
        const result = await this._cache?.getData(startDate, endDate, this.states, this.branchIds, this.userId);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  protected override getTitle(): string {
    return tlang`!!quote!! (Net Amount) - ${this.dateRange?.label}`;
  }

  protected getSeriesColorByState(state: number): string {
    // return the corresponding badge colour
    switch (state as QuoteState) {
      case QuoteState.Draft:
        return '#0ea5e9';
      case QuoteState.Active:
        return '#d946ef';
      case QuoteState.IssuePending:
      case QuoteState.SupplierReviewPending:
        return '#a855f7';
      case QuoteState.Issued:
        return '#eab308';
      case QuoteState.SupplierReviewed:
        return '#06b6d4';
      case QuoteState.Accepted:
      case QuoteState.Accepted_AssignedToReviewer:
        return '#84cc16';
      case QuoteState.Approved:
        return '#f97316';
      default:
        return '#3282cc'; // base colour
    }
  }

  protected async updateData(): Promise<void> {
    const results = await this.refreshData();
    if (!results) return;

    await this.updateComplete;
    if (!this.isConnected || this._isDisconnected) return;

    // Clear any existing charts and container before rendering
    try { this.chart?.destroy?.(); } catch (e) { /* noop */ }
    this.chart = null;
    for (const c of this.charts) { try { c?.destroy?.(); } catch (e) { /* noop */ } }
    this.charts = [];

    const parent = this.querySelector('.quote-issued-revenue');
    if (!parent || !this.isConnected || this._isDisconnected) return;
    parent.innerHTML = '';

    // Group by currency using enum->ISO mapping
    const currencyGroups: Record<string, typeof results.revenueSummary> = {} as any;
    for (const item of results.revenueSummary) {
      const currency = currencyCodeToString((item as any).currency) ?? 'USD';
      (currencyGroups[currency] ??= []).push(item as any);
    }

    // Render a chart per currency
    Object.entries(currencyGroups).forEach(([currency, revenueSummary]) => {
      if (!this.isConnected || this._isDisconnected) return;

      const chartSeries: ChartSeriesXY[] = [];
      for (const quoteState of results.states) {
        const chartData: ChartDataXY[] = [];
        const data = revenueSummary.filter(d => d.state === quoteState || d.state === -1);
        data.forEach(r => chartData.push({ x: this.convertDateToMillis(r.date), y: r.dailyRevenue }));
        chartSeries.push({
          name: getQuoteStatusStr(quoteState),
          data: chartData,
          color: this.getSeriesColorByState(quoteState)
        });
      }

      const chartOptions = {
        chart: {
          type: 'area',
          height: '179px',
          selection: { enabled: false },
          stacked: false,
          toolbar: {
            tools: {
              download: false,
              customIcons: [
                {
                  icon: '<i class="fa fa-plus-circle fa-lg"></i>',
                  index: 0,
                  title: 'Zoom',
                  class: 'custom-icon',
                  click: (chart: any, opts: any, e: any) => this.onChartClick(e, chart, opts)
                }
              ]
            }
          },
          zoom: { enabled: false }
        },
        stroke: { width: 1 },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } }
        },
        series: [],
        xaxis: { type: 'datetime' },
        yaxis: { labels: { formatter: (value: number) => `${moneyToCompactStr(value, stringToCurrencyCode(currency))}` } },
        noData: { text: 'Loading...' }
      } as any;

      // Build DOM per currency
      const parentEl = this.querySelector('.quote-issued-revenue');
      if (!parentEl || !this.isConnected || this._isDisconnected) return;
      const container = document.createElement('div');
      container.className = 'quote-issued-revenue-currency';
      const caption = document.createElement('div');
      caption.textContent = `${this.getTitle()} (${currency})`;
      caption.style.fontWeight = '600';
      caption.style.margin = '8px 0';
      container.appendChild(caption);
      parentEl.appendChild(container);

      const options = { ...chartOptions, series: chartSeries };
      const chart = new ApexCharts(container, options);
      chart.render();
      this.charts.push(chart);
    });
  }
}

const getQuoteStatusStr = (state: number) => {
  const value = QuoteState[state].toLowerCase();
  return glossary(`quote-state-${value}`);
};
